import "./MessageLanguageRow.css";

import * as React from "react";

import {
	CloseIcon,
	TableCell,
	TableRow,
	Tooltip,
} from "../../../common/MaterialComponent";
import {
	LanguageMessageItem,
	Message,
} from "../../../common/batchNotificationInterface";

import Alert from "@material-ui/lab/Alert";
import FileUpload from "../FileUpload/FileUpload";
import { ILanguage } from "../../../common/interfaces";
import { IconButtonPrimary } from "../../../common/sharedStyle";
import { LanguageMessagePreview } from "../LanguageMessagePreview/LanguageMessagePreview";
import MessageTitle from "../MessageTitle/MessageTitle";
import Shared from "../../../common/shared";
import { useGetComponentStringsContext } from "../../../hooks/useGetComponentStrings";
import TemplateSelection from "../TemplateSelection/TemplateSelection";
import { ContentTemplate } from "../../../models/BatchMessage/ContentTemplate";

export interface MessageLanguageRowProps {
	languages: ILanguage[];
	item: LanguageMessageItem;
	onTitleChanged: (title: string) => void;
	onMessageChanged: (messageDetail: Message) => void;
	onRemoveClicked?: () => void;
	onTemplateSelected: (messageDetail: Message, language: string) => void;
	isReadOnly: boolean;
	rowIndex: string;
	showRemoveRowButton: boolean;
	isBatchMessageTypeSMS: boolean;
	isBatchMessageTypeWhatsApp: boolean;
	partitionKey: string;
	highlightRequiredDetail: boolean;
	country: string;
	templates: ContentTemplate[];
}

export const MessageLanguageRow: React.FC<MessageLanguageRowProps> = ({
	item,
	languages,
	onTitleChanged,
	onMessageChanged,
	onTemplateSelected,
	onRemoveClicked,
	isReadOnly,
	rowIndex,
	showRemoveRowButton,
	isBatchMessageTypeSMS,
	country,
	isBatchMessageTypeWhatsApp,
	partitionKey,
	highlightRequiredDetail,
	templates,
}) => {
	const strings = useGetComponentStringsContext().MessageLanguageRow;

	const [showBadVariableWarning, setShowBadVariableWarning] = React.useState(
		false
	);

	const onMessageDetailChanged = (
		language: ILanguage,
		message: string,
		fileName: string = "",
		audioDuration: number = 0
	) => {
		const id =
			item.messages.find((m) => m.languageCode === language.code)?.id || "";
		const messageDetail: Message = {
			id: id,
			languageCode: language.code,
			message: message,
			fileName: fileName,
			audioDuration: audioDuration,
		};
		onMessageChanged(messageDetail);
	};

	return (
		<TableRow data-testid="messageLanguageRowContainer">
			<TableCell component="th" scope="row" className="messageTitleHeader">
				<MessageTitle
					messageTitleValue={item.title}
					onMessageContentChange={(value: string) => onTitleChanged(value)}
					isReadOnly={isReadOnly}
				/>
			</TableCell>
			{isBatchMessageTypeWhatsApp && (
				<TableCell align="right" className="tableColumn">
					<TemplateSelection
						onTemplateChange={(m: Message) => {
							onTemplateSelected(m, m.languageCode);
						}}
						country={country}
						rowId={rowIndex}
						isReadOnly={false}
						templates={templates}
					/>
				</TableCell>
			)}
			{!isBatchMessageTypeWhatsApp &&
				languages.map((l) => (
					<React.Fragment key={l.code}>
						<TableCell align="right" className="tableColumn">
							{isBatchMessageTypeSMS ? (
								<LanguageMessagePreview
									language={l}
									country={country}
									message={
										item.messages.find((m) => m.languageCode === l.code)
											?.message || ""
									}
									onMessageChanged={(message: string) =>
										onMessageDetailChanged(l, message)
									}
									isReadOnly={isReadOnly}
									rowIndex={rowIndex}
									setShowBadVariableWarning={setShowBadVariableWarning}
								/>
							) : (
								<FileUpload
									message={
										item.messages.find((m) => m.languageCode === l.code)
											?.message || ""
									}
									audioDuration={
										item.messages.find((m) => m.languageCode === l.code)
											?.audioDuration || 0
									}
									fileName={
										item.messages.find((m) => m.languageCode === l.code)
											?.fileName || ""
									}
									index={rowIndex + l.code}
									removeUploadedFile={() => onMessageDetailChanged(l, "")}
									partitionKey={partitionKey}
									onFileChange={(
										message: string,
										fileName: string,
										audioDuration: number
									) =>
										onMessageDetailChanged(l, message, fileName, audioDuration)
									}
									isReadOnly={isReadOnly}
									highlightRequiredDetail={highlightRequiredDetail}
								/>
							)}
						</TableCell>
						{/* Display information message about segment and character differences
					if the message has variables */}
						{(Shared.messageHasVariables(item, l) ||
							Shared.messageDynamicVariables(item, l).length > 0) && (
							<TableCell className="tableColumn">
								<Alert severity="info" variant="outlined">
									{strings.variablesMessageSegmentWarning}
								</Alert>
								{showBadVariableWarning && (
									<Alert severity="warning" variant="outlined">
										{strings.badVariableMessageWarning}
									</Alert>
								)}
							</TableCell>
						)}
					</React.Fragment>
				))}
			<TableCell className="deleteRowBtnColumn">
				{onRemoveClicked && showRemoveRowButton ? (
					<Tooltip title={strings.removeMessageToolTip}>
						<span>
							<IconButtonPrimary
								data-testid="removeMessage"
								className="closeIcon"
								onClick={() => onRemoveClicked()}
								disabled={isReadOnly}
							>
								<CloseIcon />
							</IconButtonPrimary>
						</span>
					</Tooltip>
				) : null}
			</TableCell>
		</TableRow>
	);
};
